import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Divider,
  IconButton,
  Tooltip,
  Avatar,
  Autocomplete,
  alpha,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import { CameraAlt } from '@mui/icons-material';
import ImageUpload from '../../ImageUpload';
import SocialLinksInput from '../SocialLinksInput';
import { 
  XIcon,
  TelegramIcon,
  DiscordIcon,
  InstagramIcon,
  GitHubIcon,
  LinkedInIcon,
  WebIcon } from '../../icons/SocialIcons';
import { PREDEFINED_ROLES } from '../roles';

interface MyAccountTabProps {
  isDark: boolean;
  colors: any;
  discordFont: any;
  glassStyle: any;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  formData: any;
  handleFormChange: (field: string, value: any) => void;
  handleImageSelect: (file: File, type: 'profile' | 'banner') => void;
  user: any;
  profileImageFile: File | null;
  bannerImageFile: File | null;
  setShowPasswordDialog: (show: boolean) => void;
  isEmailRevealed: boolean;
  lokiGradient: string;
  goldColor: string;
}

const getSocialIcon = (platform: string, color: string) => {
  switch (platform) {
    case 'twitter': 
      return <XIcon style={{ color }} />;
    case 'telegram': 
      return <TelegramIcon style={{ color: '#229ED9' }} />;
    case 'discord': 
      return <DiscordIcon style={{ color: '#5865F2' }} />;
    case 'instagram': 
      return <InstagramIcon style={{ color: '#E4405F' }} />;
    case 'github': 
      return <GitHubIcon style={{ color }} />;
    case 'linkedin': 
      return <LinkedInIcon style={{ color: '#0A66C2' }} />;
    default: 
      return <WebIcon style={{ color }} />;
  }
};

const MyAccountTab: React.FC<MyAccountTabProps> = ({
  isDark,
  colors,
  discordFont,
  glassStyle,
  isEditing,
  setIsEditing,
  formData,
  handleFormChange,
  handleImageSelect,
  user,
  profileImageFile,
  bannerImageFile,
  setShowPasswordDialog,
  isEmailRevealed,
  lokiGradient,
  goldColor,
}) => {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('md'));

  const inputStyles = {
    disableUnderline: true,
    style: {
      borderRadius: '12px',
      backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
      color: colors.textPrimary,
      paddingTop: '10px',
      paddingBottom: '10px',
      height: 'auto',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
    }
  };

  const displayUsername = isEditing ? formData.username : user.username;
  const displayProfilePicture = isEditing && profileImageFile ? URL.createObjectURL(profileImageFile) : user.profilePicture;
  const displayBannerImage = isEditing && bannerImageFile ? URL.createObjectURL(bannerImageFile) : user.bannerImage;

  return (
    <>
      <Typography variant="h5" sx={{ 
        mb: 3, 
        ...discordFont, 
        color: colors.textPrimary,
        fontSize: isMobile ? '1.5rem' : '2rem',
        textAlign: 'left'
      }}>
        My Account
      </Typography>

      {/* HEADER CARD */}
      <Paper elevation={0} sx={{ 
        bgcolor: 'transparent', 
        mb: 4, 
        borderRadius: isMobile ? '16px' : '24px', 
        overflow: 'hidden' 
      }}>
        <Box sx={{ 
          borderRadius: isMobile ? '16px' : '24px', 
          overflow: 'hidden', 
          bgcolor: colors.headerBg,
          ...glassStyle
        }}>
          {/* Banner Area - Smaller on mobile */}
          <Box sx={{ 
            height: isMobile ? 100 : 140, 
            minHeight: isMobile ? 100 : 140,
            width: '100%',
            bgcolor: formData.bannerImage ? 'transparent' : colors.accent,
            backgroundImage: displayBannerImage ? `url(${displayBannerImage})` : `linear-gradient(90deg, ${colors.accent}, #0F3B2E)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            '&:hover .banner-overlay': { opacity: 1 } 
          }}>
            {/* BANNER EDIT OVERLAY */}
            {isEditing && (
              <Box 
                className="banner-overlay"
                sx={{ 
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  zIndex: 20, 
                  bgcolor: 'rgba(0,0,0,0.5)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.2s ease-in-out',
                  cursor: 'pointer'
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#fff' }}>
                  <CameraAlt sx={{ fontSize: isMobile ? 30 : 40, mb: 1 }} />
                </Box>
                
                {/* Invisible Input Layer */}
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0 }}>
                  <ImageUpload 
                    type="banner" 
                    currentImage={user.bannerImage} 
                    onImageChange={(file) => handleImageSelect(file, 'banner')} 
                  />
                </Box>
              </Box>
            )}
          </Box>

          {/* Profile Bar - Stacked on mobile */}
          <Box sx={{ 
            p: isMobile ? 2 : 3, 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center', 
            justifyContent: 'space-between', 
            position: 'relative', 
            zIndex: 20 
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: isMobile ? 'flex-start' : 'center', 
              gap: isMobile ? 1.5 : 2,
              width: isMobile ? '100%' : 'auto',
              mb: isMobile ? 2 : 0
            }}>
              {/* Avatar - Smaller and moved up/left on mobile */}
              <Box sx={{ 
                mt: isMobile ? -6 : -8, 
                position: 'relative',
                ml: isMobile ? 1 : 0,
                alignSelf: isMobile ? 'flex-start' : 'center'
              }}>
                <Box sx={{ 
                  width: isMobile ? 80 : 100, 
                  height: isMobile ? 80 : 100, 
                  position: 'relative',
                  zIndex: 22,
                  borderRadius: '50%',
                  border: `8px solid ${isDark ? colors.pageBg : '#fff'}`,
                  bgcolor: colors.pageBg,
                  overflow: 'hidden'
                }}>
                  <Avatar 
                    src={displayProfilePicture} 
                    sx={{ width: '100%', height: '100%' }} 
                  />
                  {isEditing && (
                    <Box sx={{ 
                      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                      bgcolor: 'rgba(0,0,0,0.6)', 
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 25
                    }}>
                      <ImageUpload 
                        type="profile" 
                        currentImage={user.profilePicture} 
                        onImageChange={(file) => handleImageSelect(file, 'profile')} 
                      />
                    </Box>
                  )}
                </Box>
                {!isEditing && (
                  <Box sx={{ 
                    position: 'absolute', 
                    bottom: isMobile ? 6 : 8, 
                    right: isMobile ? 6 : 8, 
                    width: isMobile ? 20 : 24, 
                    height: isMobile ? 20 : 24, 
                    bgcolor: colors.success, 
                    border: `5px solid ${isDark ? colors.pageBg : '#fff'}`, 
                    borderRadius: '50%',
                    zIndex: 30
                  }} />
                )}
              </Box>
              
              {/* Username and Bio - Better spacing on mobile */}
              <Box sx={{ 
                mt: isMobile ? -1 : -2, 
                position: 'relative', 
                zIndex: 20,
                flex: 1,
                overflow: 'hidden'
              }}>
                <Typography variant={isMobile ? "h6" : "h4"} sx={{ 
                  ...discordFont, 
                  color: colors.textPrimary, 
                  lineHeight: 1.2,
                  fontSize: isMobile ? '1.25rem' : '1.8rem',
                  wordBreak: 'break-word',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {displayUsername}
                </Typography>
                {user.bio && (
                  <Typography variant="body2" color={colors.textSecondary} sx={{ 
                    mt: 0.5, 
                    whiteSpace: 'pre-wrap', 
                    fontSize: isMobile ? '0.75rem' : '0.90rem',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {user.bio}
                  </Typography>
                )}
              </Box>
            </Box>
            
            {/* Edit Button - Full width on mobile */}
            <Button 
              variant="contained" 
              onClick={() => setIsEditing(!isEditing)}
              sx={{ 
                background: lokiGradient,
                color: goldColor, 
                textTransform: 'none', 
                fontWeight: 700,
                borderRadius: '20px',
                px: 3,
                width: isMobile ? '100%' : 'auto',
                mt: isMobile ? 1 : 0,
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                border: `1px solid ${alpha(goldColor, 0.2)}`,
                position: 'relative',
                zIndex: 20,
                fontSize: isMobile ? '0.9rem' : 'inherit'
              }}
            >
              {isEditing ? 'Cancel Editing' : 'Edit User Profile'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* INFO FIELDS */}
      <Paper elevation={0} sx={{ 
        bgcolor: colors.cardBg, 
        borderRadius: isMobile ? '16px' : '24px', 
        p: isMobile ? 2 : 4, 
        mb: 4, 
        ...glassStyle 
      }}>
        {isEditing && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" fontWeight={700} color={colors.textSecondary} sx={{ 
              textTransform: 'uppercase', 
              display: 'block', 
              mb: 1,
              fontSize: isMobile ? '0.7rem' : 'inherit'
            }}>
              About Me
            </Typography>
            <TextField 
              fullWidth 
              multiline 
              minRows={isMobile ? 2 : 3} 
              variant="filled"
              value={formData.bio}
              onChange={(e) => handleFormChange('bio', e.target.value)}
              placeholder="Write something about yourself..."
              InputProps={inputStyles}
            />
          </Box>
        )}

        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          mb: 3 
        }}>
          <Box sx={{ 
            flex: 1, 
            mr: isMobile ? 0 : 2,
            mb: isMobile ? 2 : 0,
            width: '100%'
          }}>
            <Typography variant="caption" fontWeight={700} color={colors.textSecondary} sx={{ 
              textTransform: 'uppercase', 
              display: 'block', 
              mb: 1,
              fontSize: isMobile ? '0.7rem' : 'inherit'
            }}>
              Display Name
            </Typography>
            {isEditing ? (
              <TextField 
                fullWidth 
                variant="filled"
                value={formData.username} 
                onChange={(e) => handleFormChange('username', e.target.value)}
                InputProps={inputStyles}
              />
            ) : (
              <Typography variant="body1" color={colors.textPrimary}>{displayUsername}</Typography>
            )}
          </Box>
          {!isEditing && (
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => setIsEditing(true)} 
              sx={{ 
                color: colors.accent, 
                borderColor: colors.accent, 
                borderRadius: '20px',
                alignSelf: isMobile ? 'flex-end' : 'auto',
                mt: -5
              }}
            >
              Edit
            </Button>
          )}
        </Box>

        {/* ROLE SECTION */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          mb: 3 
        }}>
          <Box sx={{ 
            flex: 1, 
            mr: isMobile ? 0 : 2,
            width: '100%'
          }}>
            <Typography variant="caption" fontWeight={700} color={colors.textSecondary} sx={{ 
              textTransform: 'uppercase', 
              display: 'block', 
              mb: 1,
              fontSize: isMobile ? '0.7rem' : 'inherit'
            }}>
              Role
            </Typography>
            {isEditing ? (
              <Autocomplete
                freeSolo
                options={PREDEFINED_ROLES}
                value={formData.role}
                onChange={(event, newValue) => handleFormChange('role', newValue)}
                onInputChange={(event, newInputValue) => handleFormChange('role', newInputValue)}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    variant="filled" 
                    InputProps={{ 
                      ...params.InputProps, 
                      ...inputStyles,
                      disableUnderline: true 
                    }} 
                  />
                )}
                ListboxProps={{
                  sx: {
                    bgcolor: isDark ? '#1E1F22' : '#FFFFFF',
                    color: colors.textPrimary,
                    border: `1px solid ${colors.borderColor}`,
                    borderRadius: '12px',
                    mt: 1,
                    '& .MuiAutocomplete-option': {
                      '&:hover': { bgcolor: isDark ? alpha(colors.accent, 0.1) : '#F2F3F5' },
                      '&[aria-selected="true"]': { bgcolor: isDark ? alpha(colors.accent, 0.2) : '#E3E5E8' },
                    }
                  }
                }}
              />
            ) : (
              <Typography variant="body1" color={colors.textPrimary}>
                {user.userRole || 'No role set'}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Email */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center', 
            mb: 1 
          }}>
            <Typography variant="caption" fontWeight={700} color={colors.textSecondary} sx={{ 
              textTransform: 'uppercase',
              fontSize: isMobile ? '0.7rem' : 'inherit'
            }}>
              Email
            </Typography>
            {!isEditing && !isEmailRevealed && (
              <Typography 
                variant="caption" 
                color={colors.accent} 
                sx={{ 
                  cursor: 'pointer', 
                  fontWeight: 700,
                  mt: isMobile ? 0.5 : 0
                }}
                onClick={() => setShowPasswordDialog(true)}
              >
                Reveal Password
              </Typography>
            )}
          </Box>
          <Box sx={{ 
            p: isMobile ? 1.5 : 2, 
            bgcolor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)', 
            borderRadius: '12px', 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center', 
            justifyContent: 'space-between',
            gap: isMobile ? 1 : 0
          }}>
            <Typography variant="body1" color={colors.textPrimary} sx={{ mb: isMobile ? 1 : 0 }}>
              {isEmailRevealed ? user.email : user.email.replace(/(.{2})(.*)(?=@)/, "$1***")}
            </Typography>
            <Button 
              variant="outlined" 
              size="small" 
              disabled 
              sx={{ 
                borderRadius: '20px', 
                borderColor: alpha(colors.textSecondary, 0.2), 
                color: colors.textSecondary,
                alignSelf: isMobile ? 'flex-end' : 'auto'
              }}
            >
              Edit
            </Button>
          </Box>
        </Box>

        {/* Phone */}
        <Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center', 
            mb: 1 
          }}>
            <Typography variant="caption" fontWeight={700} color={colors.textSecondary} sx={{ 
              textTransform: 'uppercase',
              fontSize: isMobile ? '0.7rem' : 'inherit'
            }}>
              Phone Number
            </Typography>
          </Box>
          <Box sx={{ 
            p: isMobile ? 1.5 : 2, 
            bgcolor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)', 
            borderRadius: '12px', 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center', 
            justifyContent: 'space-between',
            gap: isMobile ? 1 : 0
          }}>
            <Typography variant="body1" color={colors.textPrimary} sx={{ mb: isMobile ? 1 : 0 }}>
              {/* Empty as requested */}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-end' : 'center', 
              gap: isMobile ? 1 : 2 
            }}>
              <Typography variant="caption" color={colors.textSecondary} sx={{ 
                fontStyle: 'italic',
                fontSize: isMobile ? '0.75rem' : 'inherit'
              }}>
                Coming Soon
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                disabled 
                sx={{ 
                  color: colors.textSecondary, 
                  borderColor: alpha(colors.textSecondary, 0.3), 
                  borderRadius: '20px',
                  fontSize: isMobile ? '0.75rem' : 'inherit'
                }}
              >
                Add
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Divider sx={{ borderColor: colors.borderColor, mb: 4 }} />

      {/* CONNECTIONS */}
      <Typography variant="h6" fontWeight={700} color={colors.textPrimary} sx={{ 
        mb: 2,
        fontSize: isMobile ? '1rem' : 'inherit'
      }}>
        Connections
      </Typography>
      
      {isEditing ? (
        <Box sx={{ mb: 8 }}>
          <SocialLinksInput 
            socialLinks={formData.socialLinks || {}}
            onChange={(links) => handleFormChange('socialLinks', links)}
            disabled={!isEditing} 
          />
        </Box>
      ) : (
        <Paper elevation={0} sx={{ 
          bgcolor: colors.cardBg, 
          borderRadius: isMobile ? '16px' : '24px', 
          p: isMobile ? 2 : 3, 
          mb: 4, 
          ...glassStyle 
        }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-start' }}>
            {Object.entries(user.socialLinks || {}).map(([platform, url]) => {
              if (!url || typeof url !== 'string' || url.trim() === '') return null;
              const finalUrl = url.includes('://') ? url : platform === 'discord' ? '#' : `https://${platform}.com/${url}`;
              return (
                <Tooltip key={platform} title={platform.charAt(0).toUpperCase() + platform.slice(1)}>
                  <IconButton
                    component="a"
                    href={finalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      bgcolor: isDark ? 'rgba(0,0,0,0.2)' : '#fff',
                      border: `1px solid ${alpha(colors.textSecondary, 0.2)}`,
                      '&:hover': { bgcolor: alpha(colors.accent, 0.1), borderColor: colors.accent },
                      width: isMobile ? 40 : 48,
                      height: isMobile ? 40 : 48
                    }}
                  >
                    {getSocialIcon(platform, colors.iconColor)}
                  </IconButton>
                </Tooltip>
              );
            })}
            {Object.values(user.socialLinks || {}).every(url => !url) && (
              <Typography variant="body2" color={colors.textSecondary} sx={{ textAlign: isMobile ? 'center' : 'left', width: '100%' }}>
                No connections added.
              </Typography>
            )}
          </Box>
        </Paper>
      )}
    </>
  );
};

export default MyAccountTab;